import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { conversationService, type ConversationMessage } from '@/services/conversationService';

interface StreamChunk {
  type: 'text_delta' | 'usage' | 'done' | 'error' | 'tool_start' | 'tool_done';
  text?: string;
  error?: string;
  toolName?: string;
  toolResult?: string;
}

function toolLabel(name: string): string {
  const labels: Record<string, string> = {
    get_weather: '🌤 Checking weather...',
    search_web: '🔍 Searching the web...',
    get_current_datetime: '🕐 Getting time...',
  };
  return labels[name] || `Running ${name}...`;
}

interface ChatPanelProps {
  conversationId: string;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ conversationId }) => {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeTools, setActiveTools] = useState<string[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Load conversation history when conversationId changes
  useEffect(() => {
    let cancelled = false;
    setIsLoadingHistory(true);
    setMessages([]);
    conversationService.get(conversationId).then((conv) => {
      if (!cancelled) {
        setMessages(conv.messages ?? []);
        setIsLoadingHistory(false);
      }
    }).catch(() => {
      if (!cancelled) setIsLoadingHistory(false);
    });
    return () => { cancelled = true; };
  }, [conversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  const handleSend = useCallback(
    async (text: string) => {
      // Add user message optimistically
      const userMsg: ConversationMessage = {
        id: `temp-${Date.now()}`,
        conversation_id: conversationId,
        role: 'user',
        content: text,
        token_count: null,
        is_summary: false,
        metadata: null,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setStreamingText('');
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await conversationService.sendMessageStream(conversationId, text, controller.signal);
        const reader = res.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';
        let fullText = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice(6).trim();
            if (!jsonStr) continue;

            let chunk: StreamChunk | null = null;
            try {
              chunk = JSON.parse(jsonStr) as StreamChunk;
            } catch {
              continue; // skip malformed JSON
            }
            if (chunk.type === 'text_delta' && chunk.text) {
              fullText += chunk.text;
              setStreamingText(fullText);
            } else if (chunk.type === 'tool_start' && chunk.toolName) {
              setActiveTools(prev => [...prev, chunk!.toolName!]);
            } else if (chunk.type === 'tool_done' && chunk.toolName) {
              setActiveTools(prev => prev.filter(t => t !== chunk!.toolName));
            } else if (chunk.type === 'error') {
              throw new Error(chunk.error || 'Stream error');
            }
          }
        }

        // Add the complete assistant message
        const assistantMsg: ConversationMessage = {
          id: `temp-assistant-${Date.now()}`,
          conversation_id: conversationId,
          role: 'assistant',
          content: fullText,
          token_count: null,
          is_summary: false,
          metadata: null,
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMsg]);
        setStreamingText('');
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        // Show error as system message
        const errorMsg: ConversationMessage = {
          id: `temp-error-${Date.now()}`,
          conversation_id: conversationId,
          role: 'system' as any,
          content: `Error: ${(err as Error).message}`,
          token_count: null,
          is_summary: false,
          metadata: null,
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsStreaming(false);
        setActiveTools([]);
        abortRef.current = null;
      }
    },
    [conversationId],
  );

  return (
    <div className="flex flex-col h-full bg-background overflow-hidden">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-1">
        {isLoadingHistory ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            Loading messages…
          </div>
        ) : messages.length === 0 && !isStreaming ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            Start a conversation...
          </div>
        ) : null}
        {messages.map((msg) => (
          <ChatMessage key={msg.id} role={msg.role} content={msg.content} />
        ))}
        {isStreaming && streamingText && (
          <ChatMessage role="assistant" content={streamingText} />
        )}
        {isStreaming && activeTools.length > 0 && (
          <div className="flex justify-start mb-2">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2 text-xs text-amber-700 dark:text-amber-300 flex items-center gap-2">
              <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {activeTools.map(t => toolLabel(t)).join(', ')}
            </div>
          </div>
        )}
        {isStreaming && !streamingText && (
          <div className="flex justify-start mb-3">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex space-x-1.5">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <ChatInput onSend={handleSend} isLoading={isStreaming} />
    </div>
  );
};
